import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {makeAutoObservable, observable} from "mobx";
import {ModalType, RequestState} from "data/enums";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IModalsStore} from "data/stores/modals/modals.store";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import {eq} from "lodash";
import type {IRankingsStore} from "data/stores/rankings/rankings.store";

export interface IInfoSessionModalController extends ViewController {
	get i18n(): ILocalizationStore;
	get open(): boolean;
	get data(): {
		title: string;
		message: string;
		button: string;
	};

	close: () => void;
	onButtonClick: () => void;
}

@injectable()
export class InfoSessionModalController implements IInfoSessionModalController {
	@observable _requestState: RequestState = RequestState.IDLE;

	constructor(
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore,
		@inject(Bindings.LocalizationStore) private _i18nStore: ILocalizationStore,
		@inject(Bindings.RoundsStore) private _roundStore: IRoundsStore,
		@inject(Bindings.RankingsStore) private _rankingStore: IRankingsStore
	) {
		makeAutoObservable(this);
	}

	get i18n() {
		return this._i18nStore;
	}

	get modal() {
		return this._modalsStore.modal;
	}

	get open() {
		return eq(ModalType.INFO_SESSION, this.modal);
	}

	get data() {
		if (this.isLastDay) {
			return {
				title: this.i18n.t("modal.info_session.title_last", "SESSION ALREADY STARTED"),
				message: this.i18n.t(
					"modal.info_session.message_last",
					"Day {{x}} is locked and you won’t be able to pick a team in this occasion.",
					{x: this.currentDay}
				),
				button: this.i18n.t("modal.info_session.button_last", "BACK"),
			};
		}

		return {
			title: this.i18n.t("modal.info_session.title", "SESSION ALREADY STARTED"),
			message: this.i18n.t(
				"modal.info_session.message",
				"Day {{x}} is locked but you can pick a team for Day {{y}}",
				{x: this.currentDay, y: this.nextDay}
			),
			button: this.i18n.t("modal.info_session.button", "PICK TEAM"),
		};
	}

	get selectedRoundIndex() {
		return this._roundStore.selectedRoundIndex;
	}

	get currentDay() {
		return this.selectedRoundIndex + 1;
	}

	get nextDay() {
		return this.currentDay + 1;
	}

	get isLastDay() {
		return this._roundStore.selectedRoundIndex === 2;
	}

	private onChangeRound = (roundId: number) => {
		this._roundStore.setSelectedRoundId(roundId);
		this._roundStore.setSelectedRoundSessions();
		this._roundStore.setSelectedSessionId(this._roundStore.selectedRoundSessions[0].id ?? 0);
		void this._rankingStore.fetchGamebar({
			gamedayId: roundId,
		});
	};

	onButtonClick = () => {
		if (this.isLastDay) {
			this.close();
			return;
		}

		this.onChangeRound(this._roundStore.list[this.selectedRoundIndex + 1].id ?? 0);
		this.close();
	};

	close = () => {
		this._modalsStore.hideModal();
	};

	dispose() {
		return;
	}

	init(param: void) {
		return;
	}
}
